import { useEffect, useRef, useState } from "react";

/* true = hide (scrolling down), false = show (scrolling up / near top) */
export default function useHideOnScrollDown({
  paused = false,
  threshold = 10,
  resetKey,
} = {}) {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(null); // null = "take a fresh reading on the next scroll"
  const ticking = useRef(false);
  const pausedRef = useRef(paused);

  // force-show while paused (e.g. a dropdown is open)
  useEffect(() => {
    pausedRef.current = paused;
    if (paused) setHidden(false);
  }, [paused]);

  // always show after navigating
  useEffect(() => {
    setHidden(false);
    lastY.current = null; // no layout read here
  }, [resetKey]);

  useEffect(() => {
    const update = () => {
      ticking.current = false;

      const maxY = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        0,
      );
      // clamp to ignore iOS rubber-band overscroll
      const y = Math.min(Math.max(window.scrollY, 0), maxY);

      // paused, or first scroll since mount/navigation: just record position
      if (pausedRef.current || lastY.current === null) {
        lastY.current = y;
        return;
      }

      const diff = y - lastY.current;

      if (y < 10) {
        setHidden(false);
        lastY.current = y;
      } else if (Math.abs(diff) >= threshold) {
        setHidden(diff > 0);
        lastY.current = y;
      }
    };

    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return hidden;
}
